import React from 'react';
import { motion } from 'framer-motion';
import { 
  Globe, 
  Smartphone, 
  Megaphone, 
  Wrench, 
  Cloud, 
  ShieldCheck, 
  ChevronRight,
  Eye,
  MessageCircle,
  Facebook,
  Instagram
} from 'lucide-react';
import { Link } from 'react-router-dom';
// social icons removed (unused in this page)
import { Icon3DGlobe, Icon3DMobile, Icon3DMegaphone, Icon3DCloud, Icon3DWrench } from '../components/3DIcon';

const Services = () => {
  const shareWhatsApp = (title: string, path: string) => {
    const url = window.location.origin + path;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(title)}%20${encodeURIComponent(url)}`, '_blank');
  };

  const shareFB = (path: string) => {
    const url = window.location.origin + path;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareIG = (title: string, path: string) => {
    const url = window.location.origin + path;
    if (navigator.share) {
      navigator.share({ title, url }).catch(console.error);
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied for Instagram!');
    }
  };

  const serviceCategories = [
    {
      title: 'Web Development',
      icon: Globe,
      icon3D: Icon3DGlobe,
      color: 'from-blue-500 to-indigo-600',
      path: '/services/web',
      desc: 'High-performance, scalable, and visually engaging websites that reflect your brand identity.',
      features: ['UI/UX Design', 'Custom Web Apps', 'CMS Solutions', 'ERP Integration', 'E-commerce Stores']
    },
    {
      title: 'Mobile Development',
      icon: Smartphone,
      icon3D: Icon3DMobile,
      color: 'from-pink-500 to-rose-600',
      path: '/services/mobile',
      desc: 'Powerful and user-centric mobile applications for Android and iOS that drive engagement.',
      features: ['Native iOS Apps', 'Native Android Apps', 'Cross-Platform Apps', 'App Store Optimization']
    },
    {
      title: 'Digital Marketing',
      icon: Megaphone,
      icon3D: Icon3DMegaphone,
      color: 'from-emerald-500 to-teal-600',
      path: '/services/digital-marketing',
      desc: 'Data-driven marketing strategies to boost your online visibility and convert clicks into customers.',
      features: ['SEO Mastery', 'Social Media ROI', 'PPC Management', 'Content Strategy']
    },
    {
      title: 'Cloud Solutions',
      icon: Cloud,
      icon3D: Icon3DCloud,
      color: 'from-amber-500 to-orange-600',
      path: '/services/cloud',
      desc: 'Flexible, secure, and scalable cloud infrastructure to empower your digital ecosystem.',
      features: ['AWS Architecture', 'Google Cloud Setup', 'SaaS Development', 'Cloud Migration']
    },
    {
      title: 'Maintenance & Support',
      icon: Wrench,
      icon3D: Icon3DWrench,
      color: 'from-slate-700 to-slate-900',
      path: '/services/maintenance',
      desc: 'Ensuring your digital platforms remain stable, secure, and up to date with 24/7 assistance.',
      features: ['System Optimization', 'Bug Fixing', 'Security Patching', '24/7 Monitoring']
    },
    {
      title: 'Security Solutions',
      icon: ShieldCheck,
      icon3D: Icon3DWrench,
      color: 'from-violet-500 to-purple-600',
      path: '/services/security',
      desc: 'Safeguarding your data and applications with advanced threat detection and secure controls.',
      features: ['Vulnerability Management', 'Secure Access Controls', 'Penetration Testing', 'Compliance Audits']
    }
  ];

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] transition-colors duration-500">
      {/* Header */}
      <section className="bg-white dark:bg-[#001a35] border-b border-slate-100 dark:border-white/5 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-4 block">Our Expertise</span>
            <h1 className="text-4xl md:text-5xl font-semibold text-[#002a5c] dark:text-white mb-6">Powering Your Business with Innovation</h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
              We offer a comprehensive suite of digital services designed to help you navigate the complexities of the modern technological landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceCategories.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-[#001a35] rounded-[2.5rem] p-8 shadow-sm hover:shadow-2xl transition-all border border-slate-100 dark:border-white/10 group flex flex-col h-full"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} text-white flex items-center justify-center mb-8 shadow-lg group-hover:scale-110 transition-transform overflow-hidden`}>
                  {service.icon3D ? (
                    <service.icon3D size="w-8 h-8" />
                  ) : (
                    React.createElement(service.icon, { size: 32 })
                  )}
                </div>
                <h3 className="text-2xl font-semibold text-[#002a5c] dark:text-white mb-4">{service.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8 flex-grow">
                  {service.desc}
                </p>
                
                <div className="space-y-3 mb-8">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                      <span className="text-xs font-semibold text-[#002a5c]/70 dark:text-slate-400">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Social Share Bar */}
                <div className="flex items-center gap-2 mb-6 p-2 bg-slate-50 dark:bg-white/5 rounded-xl">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 ml-2">Share:</span>
                  <div className="flex gap-1">
                    <button 
                      onClick={(e) => { e.preventDefault(); shareWhatsApp(service.title, service.path); }}
                      className="p-2 hover:bg-[#25D366]/10 text-[#25D366] rounded-lg transition-colors"
                      title="Share on WhatsApp"
                    >
                      <MessageCircle size={16} />
                    </button>
                    <button 
                      onClick={(e) => { e.preventDefault(); shareFB(service.path); }}
                      className="p-2 hover:bg-[#1877F2]/10 text-[#1877F2] rounded-lg transition-colors"
                      title="Share on Facebook"
                    >
                      <Facebook size={16} />
                    </button>
                    <button 
                      onClick={(e) => { e.preventDefault(); shareIG(service.title, service.path); }}
                      className="p-2 hover:bg-[#E4405F]/10 text-[#E4405F] rounded-lg transition-colors"
                      title="Share on Instagram"
                    >
                      <Instagram size={16} />
                    </button>
                  </div>
                </div>

                <Link 
                  to={service.path}
                  className="mt-auto flex items-center justify-between p-4 bg-slate-50 dark:bg-white/5 rounded-2xl group/btn hover:bg-blue-600 transition-all border border-transparent hover:border-blue-400 shadow-sm hover:shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover/btn:bg-white group-hover/btn:text-blue-600 transition-all">
                      <Eye size={16} />
                    </div>
                    <span className="text-sm font-semibold text-[#002a5c] dark:text-white group-hover/btn:text-white transition-colors">View Service</span>
                  </div>
                  <ChevronRight size={18} className="text-[#002a5c] dark:text-white group-hover/btn:text-white group-hover/btn:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#002a5c] text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">How We Deliver Results</h2>
            <p className="text-blue-300">A systematic approach to your digital success.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Understanding your unique business challenges and goals.' },
              { step: '02', title: 'Strategy', desc: 'Engineering a bespoke technical roadmap for your project.' },
              { step: '03', title: 'Development', desc: 'Building your solution with clean code and modern tech.' },
              { step: '04', title: 'Launch & Support', desc: 'Seamless deployment and ongoing performance optimization.' }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-white/5 absolute -top-10 -left-4">
                  {item.step}
                </div>
                <h4 className="text-xl font-semibold mb-4 relative z-10">{item.title}</h4>
                <p className="text-sm text-blue-200 leading-relaxed relative z-10">{item.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-[1px] bg-blue-500/30" />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-transparent pointer-events-none" />
      </section>

      {/* Contact CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white dark:bg-[#001a35] rounded-[3rem] p-12 shadow-xl border border-slate-100 dark:border-white/10 transition-colors duration-500">
            <h2 className="text-3xl font-semibold text-[#002a5c] dark:text-white mb-6">Need a Custom Solution?</h2>
            <p className="text-slate-50 dark:text-slate-400 mb-10 leading-relaxed">
              Every business is different. We specialize in building custom applications tailored specifically to your operational needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/quote" className="w-full sm:w-auto px-10 py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 text-center">
                Get a Free Quote
              </Link>
              <Link to="/contact" className="w-full sm:w-auto px-10 py-4 bg-slate-50 dark:bg-white/5 text-[#002a5c] dark:text-white rounded-2xl font-semibold hover:bg-slate-100 dark:hover:bg-white/10 transition-all">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
